import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import Card from '../components/Card'

export default function InvoiceNew() {
  const navigate = useNavigate()
  const api = useApi()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usage, setUsage] = useState<{
    active_invoices: number
    active_invoices_limit: number
  } | null>(null)

  useEffect(() => {
    api
      .getInvoices()
      .then((data) => {
        if (data.usage) setUsage(data.usage)
      })
      .catch(() => {
        // silently ignore
      })
  }, [])

  const atLimit = usage ? usage.active_invoices >= usage.active_invoices_limit : false

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const form = e.currentTarget
    const formData = new FormData(form)

    const amountDollars = parseFloat(formData.get('amount') as string)
    const amountCents = Math.round(amountDollars * 100)

    try {
      const data = await api.createInvoice({
        client_name: formData.get('client_name'),
        client_email: formData.get('client_email'),
        invoice_label: formData.get('invoice_label'),
        invoice_amount: amountCents,
        currency: formData.get('currency'),
        invoice_link: formData.get('invoice_link'),
        due_date: formData.get('due_date'),
        tone: formData.get('tone'),
        notes: formData.get('notes') || null,
      })
      navigate(`/invoices/${data.invoice.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">New invoice</h1>

      {atLimit && (
        <Card className="border-ica-amber/20 bg-ica-amber/5">
          <p className="text-sm font-medium text-ica-amber">Beta limit reached</p>
          <p className="text-xs text-ica-text-muted">
            You can have up to {usage?.active_invoices_limit} active invoices.{" "}
            <a href="/" className="text-ica-accent hover:underline">Upgrade to add more.</a>
          </p>
        </Card>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <Card className="border-ica-red/20 bg-ica-red/5 text-sm text-ica-red">{error}</Card>
        )}

        <Card className="space-y-4">
          <p className="text-sm font-semibold text-ica-text-secondary">Client</p>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Client name</label>
              <input
                name="client_name"
                required
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Client email</label>
              <input
                name="client_email"
                type="email"
                required
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
          </div>
        </Card>

        <Card className="space-y-4">
          <p className="text-sm font-semibold text-ica-text-secondary">Invoice details</p>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Invoice label</label>
              <input
                name="invoice_label"
                required
                placeholder="e.g., Website redesign — March"
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Amount</label>
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Currency</label>
                <select
                  name="currency"
                  className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Due date</label>
              <input
                name="due_date"
                type="date"
                required
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Invoice link</label>
              <input
                name="invoice_link"
                type="url"
                placeholder="https://..."
                required
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
          </div>
        </Card>

        <Card className="space-y-4">
          <p className="text-sm font-semibold text-ica-text-secondary">Reminder settings</p>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Tone</label>
              <select
                name="tone"
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              >
                <option value="friendly">Friendly</option>
                <option value="firm">Firm</option>
                <option value="final">Final</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Notes</label>
              <textarea
                name="notes"
                rows={3}
                className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
              />
            </div>
          </div>
        </Card>

        <button
          type="submit"
          disabled={loading || atLimit}
          className="w-full rounded-xl bg-ica-accent px-4 py-3 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading ? 'Saving...' : atLimit ? 'Limit reached' : 'Save invoice'}
        </button>
      </form>
    </div>
  )
}
